import { StrictMode } from "react";
import { BProgress } from "@bprogress/core";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import "@bprogress/core/css";
import "@roqua/components/styles";

// Import the generated route tree
import { trackRouteChange } from "./analytics/wrappers";
import { appsignal } from "./appsignal";
import { toError } from "./lib/utils";
import { ErrorFallback } from "./components/ErrorBoundary";
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: false,
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: ({ error }) => (
    <ErrorFallback error={error} resetErrorBoundary={() => null} />
  ),
  defaultOnCatch: (error) => {
    console.error("Error caught in router:", error);
    void appsignal.sendError(toError(error), {});
  },
});

BProgress.configure({
  showSpinner: false,
});

router.subscribe("onBeforeLoad", ({ fromLocation, pathChanged }) => {
  // Only show the progress bar on navigation (not initial page load), and only if the path has changed
  if (fromLocation && pathChanged) {
    BProgress.start();
  }
});

router.subscribe("onLoad", () => {
  BProgress.done();
});

router.subscribe("onBeforeNavigate", ({ toLocation }) => {
  appsignal.addBreadcrumb({
    category: "navigation",
    action: `Navigating to ${toLocation.href}`,
  });

  trackRouteChange(toLocation.pathname);
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

// Reload the page if there is a preload error,
// which will happen if we deploy new code
window.addEventListener("vite:preloadError", (event) => {
  window.location.reload(); // refresh the page
  event.preventDefault();
});
